import React, { FC, useState, MutableRefObject } from 'react';

import SwipeableViews from 'react-swipeable-views';

import { ScoreType } from '../../../../store/admin/eventFormats/store';
import { IScore } from '../../../../store/player/scores/store';

import { toHandicap } from '../../../../contrib/Utils/toHandicap';
import Button from '../../../Button';
import Input from '../../../Input';
import YesNoSwitch from '../../../YesNoSwitch';
import SummaryTable from './Components/SummaryTable/SummaryTable';
import { SummaryTableForm } from './Components/SummaryTableForm';
import { TabPanel } from './Components/TabPanel';
import { useRoundScoreSummaryState } from '../../Context/RoundScoreSummary.context';

interface IProps {
  editing?: boolean;
  playerIndex?: number;
  formatIsStableford?: boolean;
  fullTable?: boolean;
  hideHandicapsRow?: boolean;
  hideIndexRow?: boolean;
  hideNet?: boolean;
  onChange?: (newScores: number[]) => void;
  onChangePenalties?: (newPenalties: number) => void;
  onChangeSignature?: (isSigned: boolean) => void;
  penalties?: number;
  playerScores?: IScore;
  range: { start: number; length: number };
  scorer?: string;
  scoreType?: ScoreType;
  signed?: boolean;
  hideConfirmedSignature?: boolean;
  hideAddPenalties?: boolean;
  formRef: MutableRefObject<undefined>;
}

const RoundScoreSummaryMainTable: FC<IProps> = props => {
  const {
    editing,
    playerIndex,
    formatIsStableford,
    fullTable,
    hideNet = false,
    hideAddPenalties = false,
    hideHandicapsRow,
    hideIndexRow,
    onChange,
    onChangePenalties,
    onChangeSignature,
    penalties,
    playerScores,
    range,
    scorer,
    scoreType,
    signed,
    formRef,
    hideConfirmedSignature = false
  } = props;

  const { totalGross, totalNet } = useRoundScoreSummaryState();

  const [page, setPage] = useState(Number(range.start === 9));

  const handleChangeIndex = (index: number) => {
    setPage(index);
  };

  const togglePage = () => {
    setPage(page === 0 ? 1 : 0);
  };

  const onSign = (_: any, value: boolean) => onChangeSignature && onChangeSignature(value);
  const onSetPenalties = (e: any) => onChangePenalties && onChangePenalties(e.target.value);

  return (
    <div className="content">
      {!fullTable ? (
        <SwipeableViews
          index={page}
          dir={!page ? 'x' : 'x-reverse'}
          onChangeIndex={handleChangeIndex}
        >
          <TabPanel value={page} index={0} dir={'x'}>
            <SummaryTable
              end={9}
              formatIsStableford={formatIsStableford}
              hideHandicapsRow={hideHandicapsRow}
              partials={[{ name: 'OUT', from: 0, to: 8 }]}
              scoreType={scoreType}
              start={0}
              hideNet={hideNet}
            />
          </TabPanel>

          <TabPanel value={page} index={1} dir={'x'}>
            <SummaryTable
              end={18}
              formatIsStableford={formatIsStableford}
              hideHandicapsRow={hideHandicapsRow}
              partials={[{ name: 'IN', from: 9, to: 17 }]}
              scoreType={scoreType}
              hideNet={hideNet}
              start={9}
            />
          </TabPanel>
        </SwipeableViews>
      ) : (
        <SummaryTable
          partials={[{ name: 'OUT', from: 0, to: 8 }, { name: 'IN', from: 9, to: 17 }]}
          start={0}
          end={18}
          formatIsStableford={formatIsStableford}
          hideNet={hideNet}
        />
      )}

      <SummaryTableForm
        onChange={onChange}
        hidden={!editing}
        partials={[{ name: 'OUT', from: 0, to: 8 }, { name: 'IN', from: 9, to: 17 }]}
        start={0}
        end={18}
        ref={formRef}
      />

      <div className="stats-row">
        {!hideHandicapsRow && (
          <>
            <div className="stat-title">EVENT HANDICAP:</div>

            <div className="stat-value">{playerScores ? toHandicap(playerScores.handicap) : 0}</div>
          </>
        )}

        {!hideIndexRow && (
          <>
            <div className="stat-title">INDEX:</div>

            <div className="stat-value">{toHandicap(playerIndex)}</div>
          </>
        )}

        {!hideAddPenalties && (
          <div className="last-stat">
            <div className="stat-title">ADDED PENALTIES:</div>

            <div className="stat-value">
              <Input
                type="number"
                value={penalties}
                onChange={onSetPenalties}
                label=""
                inputProps={{ min: 0 }}
                outlined={true}
                noIcon={true}
                disabled={!editing}
              />
            </div>
          </div>
        )}
      </div>

      <div className="totals">
        {(scoreType === ScoreType.GROSS || scoreType === undefined) && (
          <div>
            <div className="total-title">GROSS</div>

            <div className="total-value">
              <div>{totalGross}</div>
            </div>
          </div>
        )}

        {(scoreType === ScoreType.NET || scoreType === undefined) && (
          <div>
            <div className="total-title">NET</div>

            <div className="total-value">
              <div>{totalNet}</div>
            </div>
          </div>
        )}

        {range.length > 9 && !fullTable && (
          <Button onClick={togglePage} color="primary">
            {page === 1 ? 'FRONT' : 'BACK'} 9
          </Button>
        )}

        {!hideConfirmedSignature && (
          <div className="scorer-signature">
            {!!scorer && (
              <div className="scorer">
                <div className="total-title">SCORER: </div>

                <div className="scorer-name">{scorer}</div>
              </div>
            )}

            <div className="signature">
              <div className="total-title">CONFIRMED SIGNATURE</div>

              <YesNoSwitch checked={signed} onChange={onSign} disabled={!editing} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoundScoreSummaryMainTable;
